import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Inbetriebnahmeprotokoll } from '../types';
import protokollService from '../services/protokollService';
import { Icon, PageHeader, LoadingScreen, Spinner, EmptyState, AnlegenButton, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { ankunft } from '../utils/ankunft';

const Inbetriebnahmeprotokolle: React.FC = () => {
  const [protokolle, setProtokolle] = useState<Inbetriebnahmeprotokoll[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => { loadProtokolle(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProtokolle = async () => {
    try {
      setProtokolle(await protokollService.getAll());
    } catch {
      toast.error('Protokolle konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (p: Inbetriebnahmeprotokoll) => {
    setOpenMenuId(null);
    const name = [p.kundeVorname, p.kundeNachnameFirma].filter(Boolean).join(' ');
    if (window.confirm(`Protokoll „${name}“ vom ${formatDate(p.datum)} löschen?`)) {
      try {
        await protokollService.delete(p.id);
        toast.success('Protokoll gelöscht');
        await loadProtokolle();
      } catch (err: any) {
        toast.error(fehlerText(err, 'Protokoll konnte nicht gelöscht werden'));
      }
    }
  };

  const handleToggleExpand = (p: Inbetriebnahmeprotokoll, isExpanded: boolean) => {
    if (!isExpanded && !p.gelesen && !p.entwurf) {
      protokollService.markGelesen(p.id);
      setProtokolle(prev => prev.map(x => x.id === p.id ? { ...x, gelesen: true } : x));
    }
    setExpandedId(isExpanded ? null : p.id);
  };

  const handleMarkUngelesen = async (p: Inbetriebnahmeprotokoll) => {
    setOpenMenuId(null);
    try {
      await protokollService.markUngelesen(p.id);
      setProtokolle(prev => prev.map(x => x.id === p.id ? { ...x, gelesen: false } : x));
    } catch (err) {
      toast.error(fehlerText(err, 'Protokoll konnte nicht als ungelesen markiert werden'));
    }
  };

  const handleAlleAlsGelesenMarkieren = async () => {
    const ids = protokolle.filter(p => !p.gelesen && !p.entwurf).map(p => p.id);
    if (ids.length === 0) return;
    try {
      await protokollService.markBulkGelesen(ids, true);
      setProtokolle(prev => prev.map(p => (ids.includes(p.id) ? { ...p, gelesen: true } : p)));
    } catch (err) {
      toast.error(fehlerText(err, 'Protokolle konnten nicht als gelesen markiert werden'));
    }
  };

  const handleDownloadPdf = async (p: Inbetriebnahmeprotokoll) => {
    setOpenMenuId(null);
    setDownloadingPdfId(p.id);
    try {
      await protokollService.downloadPdf(p.id);
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error(fehlerText(err, 'PDF konnte nicht erzeugt werden'));
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  const { sichtbar, Ende } = useStueckweise(protokolle);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Inbetriebnahmeprotokolle"
        subtitle={`${protokolle.length} Protokolle`}
        actions={<>
          {protokolle.some(p => !p.gelesen && !p.entwurf) && (
            <button
              onClick={handleAlleAlsGelesenMarkieren}
              className="px-3 py-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Alle als gelesen
            </button>
          )}
          <AnlegenButton onClick={() => navigate('/protokolle/neu')}>Protokoll anlegen</AnlegenButton>
        </>}
      />

      {protokolle.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100">
          <EmptyState icon={<Icon.Protokoll className="h-6 w-6" />} title="Noch keine Inbetriebnahmeprotokolle"
            hint="Bei der Übergabe einer Anlage hier das Protokoll erfassen."
            action={<AnlegenButton onClick={() => navigate('/protokolle/neu')}>Protokoll anlegen</AnlegenButton>} />
        </div>
      )}

      <div className="space-y-2">
        {sichtbar.map(p => {
          const kundeName = [p.kundeVorname, p.kundeNachnameFirma].filter(Boolean).join(' ');
          const isExpanded = expandedId === p.id;
          const isDownloading = downloadingPdfId === p.id;
          const isMenuOpen = openMenuId === p.id;
          const a = ankunft(`protokoll-${p.id}`);

          return (
            <div key={p.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
              {/* Header-Zeile */}
              <div className="flex items-center p-3 md:p-4">
                {/* Klickbarer Bereich */}
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleToggleExpand(p, isExpanded)}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {!p.gelesen && !p.entwurf && (
                      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" title="Ungelesen" />
                    )}
                    <span className="text-sm font-semibold text-gray-900 truncate">
                      {kundeName}
                    </span>
                    {p.entwurf && (
                      <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" title="Zwischengespeichert, noch nicht abgeschlossen">
                        Entwurf
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 mt-0.5 text-xs text-gray-500">
                    <span className="whitespace-nowrap">{formatDate(p.datum)}</span>
                    {p.teilnehmer && <span>{p.teilnehmer}</span>}
                    {p.gewaehrleistungBeginn && (
                      <span>Gewährleistung ab {formatDate(p.gewaehrleistungBeginn)}</span>
                    )}
                  </div>
                </div>

                {/* PDF-Button */}
                <button
                  onClick={() => handleDownloadPdf(p)}
                  disabled={isDownloading}
                  className="flex-shrink-0 p-2 mr-1 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Als PDF herunterladen"
                >
                  {isDownloading ? (
                    <Spinner className="h-5 w-5" />
                  ) : (
                    <Icon.Download className="h-5 w-5" />
                  )}
                </button>

                {/* Drei-Punkte-Menü */}
                <div className="relative flex-shrink-0" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    onClick={() => setOpenMenuId(isMenuOpen ? null : p.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Weitere Aktionen"
                  >
                    <Icon.Mehr className="w-5 h-5" />
                  </button>

                  {isMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                      <button
                        onClick={() => { setOpenMenuId(null); handleToggleExpand(p, isExpanded); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Eye className="w-4 h-4 text-gray-400" />
                        {isExpanded ? 'Zuklappen' : 'Details anzeigen'}
                      </button>
                      {p.gelesen && !p.entwurf && (
                        <button
                          onClick={() => handleMarkUngelesen(p)}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Icon.EyeOff className="w-4 h-4 text-gray-400" />
                          Als ungelesen markieren
                        </button>
                      )}
                      <button
                        onClick={() => { setOpenMenuId(null); navigate(`/protokolle/${p.id}/bearbeiten`); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Edit className="w-4 h-4 text-gray-400" />
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDownloadPdf(p)}
                        disabled={isDownloading}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                      >
                        <Icon.Download className="w-4 h-4 text-gray-400" />
                        PDF herunterladen
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => handleDelete(p)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  )}
                </div>

                {/* Expand-Chevron */}
                <button
                  onClick={() => handleToggleExpand(p, isExpanded)}
                  className="flex-shrink-0 p-1 text-gray-400"
                  aria-label={isExpanded ? 'Zuklappen' : 'Details anzeigen'}
                >
                  <Icon.ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </button>
              </div>

              {/* Ausgeklappte Details */}
              {isExpanded && (
                <div className="ui-aufklappen border-t border-gray-100 p-3 md:p-4 bg-gray-50 rounded-b-lg">
                  {p.teilnehmer && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Teilnehmer</h3>
                      <p className="text-sm text-gray-800">{p.teilnehmer}</p>
                    </div>
                  )}

                  {p.maengel && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Festgestellte Mängel</h3>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{p.maengel}</p>
                    </div>
                  )}

                  {p.gewaehrleistungBeginn && (
                    <div className="mb-3">
                      <h3 className="text-xs font-semibold text-gray-500 mb-1">Gewährleistung beginnt am</h3>
                      <p className="text-sm text-gray-800">{formatDate(p.gewaehrleistungBeginn)}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 mb-3">
                    {p.unterschriftBild && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Unterschrift</h3>
                        <img
                          src={p.unterschriftBild}
                          alt="Unterschrift"
                          className="border border-gray-200 rounded bg-white max-h-20"
                        />
                        {p.unterschriftName && (
                          <p className="text-xs text-gray-500 mt-1">{p.unterschriftName}</p>
                        )}
                      </div>
                    )}
                    {p.erstelltVon && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-500 mb-1">Erstellt von</h3>
                        <p className="text-sm text-gray-700">{p.erstelltVon}</p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/protokolle/${p.id}/bearbeiten`)}
                      className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors flex items-center gap-2 text-sm"
                    >
                      <Icon.Edit className="h-4 w-4" />
                      {p.entwurf ? 'Weiter ausfüllen' : 'Bearbeiten'}
                    </button>
                    <button
                      onClick={() => handleDownloadPdf(p)}
                      disabled={isDownloading}
                      className="bg-white border border-brand-200 text-brand-700 px-4 py-2 rounded-lg hover:bg-brand-50 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
                    >
                      {isDownloading ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        <Icon.Download className="h-4 w-4" />
                      )}
                      PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {Ende}
      </div>
    </div>
  );
};

export default Inbetriebnahmeprotokolle;
